import type { BilingualReasons, ComparisonInfo, NormalizedScores } from './types'

export function buildComparison (price: number, duration: number, avgPrice: number, avgDuration: number): ComparisonInfo {
    const priceDiffFromAvg = avgPrice - price
    const durationDiffFromAvg = avgDuration - duration

    const priceIndicator: ComparisonInfo["priceIndicator"] = Math.round(Math.abs(priceDiffFromAvg)) === 0
        ? 'average'
        : priceDiffFromAvg > 0
            ? `positive`
            : `negative`

    const durationIndicator: ComparisonInfo["durationIndicator"] = Math.round(Math.abs(durationDiffFromAvg)) === 0
        ? 'average'
        : durationDiffFromAvg > 0
            ? `positive`
            : `negative`

    return { priceDiffFromAvg, durationDiffFromAvg, priceIndicator, durationIndicator }
}

const BILINGUAL_REASONS = {
    paretoOptimal: { en: 'Better trade-off of price and duration', ar: 'توازن أفضل بين السعر والمدة' },
    cheaperThanAvg: { en: 'Cheaper than average', ar: 'أرخص من المتوسط' },
    fasterThanAvg: { en: 'Faster than average', ar: 'أسرع من المتوسط' },
    fewerLayovers: { en: 'Fewer layovers', ar: 'توقفات أقل' },
    matchesDeparture: { en: 'Matches preferred departure time', ar: 'يتوافق مع وقت المغادرة المفضل' },
    matchesArrival: { en: 'Matches preferred arrival time', ar: 'يتوافق مع وقت الوصول المفضل' },
    preferredAirport: { en: 'Routes via preferred airport', ar: 'يمر عبر المطار المفضل' }
} as const

export function buildReasons ({
    normalized,
    isParetoOptimal,
    isLayoverAirportMatch,
    comparison
}: {
    normalized: NormalizedScores
    isParetoOptimal: boolean
    isLayoverAirportMatch: boolean
    comparison: ComparisonInfo
}): BilingualReasons {
    const reasons: BilingualReasons = { en: [], ar: [] }

    const push = (key: keyof typeof BILINGUAL_REASONS) => {
        reasons.en.push(BILINGUAL_REASONS[key].en)
        reasons.ar.push(BILINGUAL_REASONS[key].ar)
    }

    if (isParetoOptimal) push('paretoOptimal')

    if (comparison.priceDiffFromAvg > 0) push('cheaperThanAvg')
    if (comparison.durationDiffFromAvg > 0) push('fasterThanAvg')

    if (normalized.layoverScore >= 0.9) push('fewerLayovers')
    if (normalized.departureScore >= 0.9) push('matchesDeparture')
    if (normalized.arrivalScore >= 0.9) push('matchesArrival')
    if (isLayoverAirportMatch) push('preferredAirport')

    return { en: reasons.en.slice(0, 4), ar: reasons.ar.slice(0, 4) }
}
